import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { notifyError, notifySuccess, notifyWarning } from '../utils/notify';

const initialForm = {
  userId: '',
  storeId: '',
  isActive: true,
};

const UserStoreMapping = ({ stores }) => {
  const [users, setUsers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await api.auth.getUsers();
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load users');
      }

      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      setUsers(rows);
    } catch (error) {
      notifyError(error.message || 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const fetchMappings = useCallback(async () => {
    setIsLoadingMappings(true);
    try {
      const response = await api.userStores.getAll();
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load user-store mappings');
      }

      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      setMappings(rows);
    } catch (error) {
      notifyError(error.message || 'Failed to load user-store mappings');
    } finally {
      setIsLoadingMappings(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchMappings();
  }, [fetchUsers, fetchMappings]);

  const usersForSelection = useMemo(() => {
    return users
      .filter((user) => String(user.role || '').toLowerCase() !== 'super admin')
      .sort((a, b) => String(a.fullName || '').localeCompare(String(b.fullName || '')));
  }, [users]);

  const getUserLabel = (mapping) => {
    if (mapping.userName || mapping.fullName) {
      const labelName = mapping.userName || mapping.fullName;
      const labelLogin = mapping.loginId ? ` (${mapping.loginId})` : '';
      return `${labelName}${labelLogin}`;
    }

    const mappedUser = users.find((user) => Number(user.id) === Number(mapping.userId));
    if (!mappedUser) {
      return mapping.userId || '-';
    }
    return `${mappedUser.fullName} (${mappedUser.loginId})`;
  };

  const getStoreLabel = (mapping) => {
    if (mapping.storeName) {
      const codeSuffix = mapping.storeCode ? ` (${mapping.storeCode})` : '';
      return `${mapping.storeName}${codeSuffix}`;
    }

    const mappedStore = stores.find((store) => Number(store.id) === Number(mapping.storeId));
    if (!mappedStore) {
      return mapping.storeId || '-';
    }
    return `${mappedStore.name} (${mappedStore.code})`;
  };

  const handleCreateMapping = async () => {
    if (!formData.userId || !formData.storeId) {
      notifyWarning('Please select both user and store');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        userId: Number(formData.userId),
        storeId: Number(formData.storeId),
        isActive: Boolean(formData.isActive),
      };

      const response = await api.userStores.create(payload);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to create mapping');
      }

      notifySuccess(response.message || 'User-store mapping created successfully');
      setFormData(initialForm);
      await fetchMappings();
    } catch (error) {
      notifyError(error.message || 'Failed to create mapping');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="space-y-4 md:space-y-6">
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">User Store Mapping</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs md:text-sm font-bold mb-1">User</label>
            <select
              value={formData.userId}
              onChange={(event) => setFormData({ ...formData, userId: event.target.value })}
              className="w-full p-2 border rounded text-sm md:text-base"
              disabled={isLoadingUsers}
            >
              <option value="">Select User</option>
              {usersForSelection.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.fullName} ({user.loginId})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-bold mb-1">Store</label>
            <select
              value={formData.storeId}
              onChange={(event) => setFormData({ ...formData, storeId: event.target.value })}
              className="w-full p-2 border rounded text-sm md:text-base"
            >
              <option value="">Select Store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} ({store.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-bold mb-1">Status</label>
            <select
              value={formData.isActive ? 'true' : 'false'}
              onChange={(event) => setFormData({ ...formData, isActive: event.target.value === 'true' })}
              className="w-full p-2 border rounded text-sm md:text-base"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleCreateMapping}
            disabled={isSaving}
            className="inline-flex items-center gap-2 bg-jewel-red text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition text-sm md:text-base disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i className="fas fa-link"></i>
            <span>{isSaving ? 'Saving...' : 'Map User to Store'}</span>
          </button>
        </div>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm md:text-base font-bold uppercase text-gray-500">Mapped Users</h3>
          <button
            onClick={fetchMappings}
            disabled={isLoadingMappings}
            className="text-xs md:text-sm text-jewel-red font-semibold hover:underline disabled:text-gray-400"
          >
            {isLoadingMappings ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full text-left border-collapse min-w-[760px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 md:p-3 border text-xs md:text-sm">User</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Store</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Status</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">User ID</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Store ID</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingMappings ? (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-sm text-gray-500">Loading mappings...</td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-sm text-gray-400">No user-store mappings found</td>
                </tr>
              ) : (
                mappings.map((mapping, index) => (
                  <tr key={`${mapping.userId || 'u'}-${mapping.storeId || 's'}-${index}`}>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">{getUserLabel(mapping)}</td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">{getStoreLabel(mapping)}</td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${mapping.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {mapping.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">{mapping.userId ?? '-'}</td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">{mapping.storeId ?? '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default UserStoreMapping;
